import { Survey, SurveyStatus } from "../../../redux/survey/survey.types";
import { ReactElement } from "react";
import { Skeleton } from "../../../components/ui/skeleton";
import SurveyCardCurve from "../../../assets/icons-redesign/survey-card-curve.svg?react";
import PencilIcon from "../../../assets/icons-redesign/pencil.svg?react";
import { canEditSurvey } from "../../../redux/survey/survey.helpers";
import { Link } from "react-router-dom";
import {
  fallbackImageBackgroundColor,
  surveyCardSubtitle,
  surveyStatusBgColor,
  surveyStatusTextColor,
} from "./survey-card.helpers";
import SurveyCardMenu from "./survey-card-menu.component";

export type SurveyCardProps = {
  survey?: Survey;
  index: number;
  onSurveyAction: (survey: Survey, action: 'assign-client') => void;
};

const SurveyCardStatus = ({ status }: { status?: SurveyStatus }): ReactElement => {
  if (!status) {
    return <Skeleton className="w-16 h-[26px] rounded-full" />;
  }

  return (
    <span
      className={`max-w-[78px] inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm ${surveyStatusTextColor(status)}`}
    >
      { status !== SurveyStatus.Draft && (<div className={`size-1 rounded-full mr-1 ${surveyStatusBgColor(status)}`} />)}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const SurveyCardHeader = ({survey, index, onSurveyAction}: SurveyCardProps): ReactElement => {
  const imageUrl = survey?.imageUrl;
  return (
    <div className={`relative flex flex-col justify-between aspect-[3/2] w-full ${imageUrl ? '' : fallbackImageBackgroundColor(index)}`}>
      { imageUrl && (
        <img
          src={imageUrl}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="flex justify-between items-center pt-3 px-3 z-10">
        <SurveyCardStatus status={survey?.surveyStatus} />
        <SurveyCardMenu
          survey={survey}
          onSurveyAction={onSurveyAction}
        />
      </div>
      <div className="flex flex-col z-10">
        {survey?.surveyStatus && canEditSurvey(survey.surveyStatus) && (
          <div className="size-7 rounded-full flex items-center justify-center self-end bg-gray-200 mr-3" >
            <PencilIcon className="size-3 fill-black" />
          </div>
        )}
        <SurveyCardCurve className="w-[101%] h-auto -mb-[1px]" />
      </div>
    </div>
  );
};

const SurveyCardData = ({survey}: { survey?: Survey }): ReactElement => {
  if (!survey) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="w-3/4 h-4 rounded" />
        <Skeleton className="w-1/2 h-4 rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-6 bg-white justify-end grow">
      <div className="w-3/4 text-lg leading-[22px] line-clamp-2">
        {survey.title}
      </div>
      <div className="text-xs text-gray-600">
        {surveyCardSubtitle(survey)}
      </div>
    </div>
  );
};

const SurveyCard = ({survey, index, onSurveyAction}: SurveyCardProps) => {
  if (!survey) {
    return (
      <div className="w-full h-full rounded-2xl overflow-hidden flex flex-col bg-white">
        <SurveyCardHeader
          survey={survey}
          index={index}
          onSurveyAction={onSurveyAction}
        />
        <SurveyCardData survey={survey} />
      </div>
    );
  }

  return (
    <Link
      to={(canEditSurvey(survey.surveyStatus) ? `/create-survey` : '/view-survey') + `?ext-survey-id=${survey.extSurveyId}`}
      className="w-full h-full rounded-2xl overflow-hidden flex flex-col hover:shadow-lg transition-shadow bg-white"
    >
      <SurveyCardHeader
        survey={survey}
        index={index}
        onSurveyAction={onSurveyAction}
      />
      <SurveyCardData survey={survey} />
    </Link>
  );
};

export default SurveyCard;
